import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import i18next from 'i18next';
import BookingRequestFiles, { BookingRequestFileItem } from 'components/BookingRequestFiles';

interface CustomerCommentDialogProps {
  open: boolean;
  comment: string;
  files?: BookingRequestFileItem[];
  onConfirm: () => void;
}

/**
 * Soft-block dialog so hotels acknowledge the booker's comment before editing an Epilo offer.
 */
export default function CustomerCommentDialog({ open, comment, files = [], onConfirm }: CustomerCommentDialogProps) {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (open) {
      setChecked(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        // Soft block: only the confirm button may dismiss after acknowledgment.
        if (reason === 'backdropClick') return;
      }}
      aria-labelledby="customer-comment-dialog"
    >
      <DialogTitle id="customer-comment-dialog">{i18next.t('customer-comment-dialog-title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {i18next.t('customer-comment-dialog-intro')}
        </Typography>
        {comment.trim() && (
          <Box
            sx={{
              bgcolor: '#fff8e1',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {comment}
            </Typography>
          </Box>
        )}
        <BookingRequestFiles files={files} />
        <FormControlLabel
          control={<Checkbox checked={checked} onChange={e => setChecked(e.target.checked)} />}
          label={i18next.t('customer-comment-dialog-acknowledge')}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" color="primary" fullWidth disabled={!checked} onClick={onConfirm}>
          {i18next.t('customer-comment-dialog-start-editing')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
